// Central query keys. Account-scoped keys include the effective account id so
// that switching account (including impersonation start/stop) cannot reuse
// another account's cached private data.

export const queryKeys = {
  session: ['session'] as const,
  providers: ['providers'] as const,
  sources: (accountId: string) => ['account', accountId, 'sources'] as const,
  source: (accountId: string, sourceId: string) =>
    ['account', accountId, 'source', sourceId] as const,
  channels: (accountId: string, sourceId: string) =>
    ['account', accountId, 'source', sourceId, 'channels'] as const,
  externalIntake: (accountId: string, sourceId: string) =>
    ['account', accountId, 'source', sourceId, 'intake'] as const,
  devices: (accountId: string) => ['account', accountId, 'devices'] as const,
  device: (accountId: string, publicId: string) =>
    ['account', accountId, 'device', publicId] as const,
  deviceStatus: (accountId: string, publicId: string) =>
    ['account', accountId, 'device', publicId, 'status'] as const,
  configuration: (accountId: string, publicId: string) =>
    ['account', accountId, 'device', publicId, 'configuration'] as const,
  deviceFirmware: (accountId: string, publicId: string) =>
    ['account', accountId, 'device', publicId, 'firmware'] as const,
  readings: (accountId: string, datastreamId: string, before: string | null) =>
    ['account', accountId, 'datastream', datastreamId, 'readings', before ?? 'first'] as const,
  /**
   * One range of history for a chart, loaded page by page. Keyed by the range
   * so switching back to a preset does not fetch it again.
   */
  chartReadings: (accountId: string, datastreamId: string, range: string) =>
    ['account', accountId, 'datastream', datastreamId, 'chart', range] as const,
  /** The newest readings loaded for one feed on the dashboard. */
  feedPreview: (accountId: string, datastreamId: string) =>
    ['account', accountId, 'datastream', datastreamId, 'preview'] as const,
  groups: (accountId: string) => ['account', accountId, 'groups'] as const,
  wifi: (accountId: string, publicId: string) =>
    ['account', accountId, 'device', publicId, 'wifi'] as const,
  zigbeeEnrollments: (accountId: string, publicId: string) =>
    ['account', accountId, 'device', publicId, 'zigbee'] as const,
  bleEnrollments: (accountId: string, publicId: string) =>
    ['account', accountId, 'device', publicId, 'ble'] as const,
  group: (accountId: string, groupId: string) => ['account', accountId, 'group', groupId] as const,
  groupInvites: (accountId: string, groupId: string) =>
    ['account', accountId, 'group', groupId, 'invites'] as const,
  groupViewers: (accountId: string, groupId: string) =>
    ['account', accountId, 'group', groupId, 'viewers'] as const,
  users: (accountId: string, offset: number) =>
    ['account', accountId, 'admin', 'users', offset] as const,
  /** Selectable catalog versions; the same for every owner, but session-scoped. */
  modules: (accountId: string) => ['account', accountId, 'modules'] as const,
  catalog: (accountId: string) => ['account', accountId, 'admin', 'modules'] as const,
  adminDevices: (accountId: string) => ['account', accountId, 'admin', 'devices'] as const,
  firmwareReleases: (accountId: string) => ['account', accountId, 'admin', 'firmware'] as const,
  accessPolicy: (accountId: string) => ['account', accountId, 'admin', 'access'] as const,
  audit: (accountId: string, offset: number) =>
    ['account', accountId, 'admin', 'audit', offset] as const,
} as const;
